import {createContext, useContext, useState, useEffect} from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import {publicGetLoadMorePhotosApi} from 'src/services/api/property'
import {PropertyPhotoType} from 'src/types/property'

interface ImageSliderContextProps {
  currentIndex: number
  loading: boolean
  images: PropertyPhotoType[]
  photoCount: number
  hasMorePhotos: boolean
  setCurrentIndex: (index: number) => void
  loadAllPhotos: () => Promise<void>
  loadMorePhotos: (index?: number) => Promise<void>
  setLoading: (loading: boolean) => void
}

export const ImageSliderContext = createContext<
  ImageSliderContextProps | undefined
>(undefined)

ImageSliderContext.displayName = 'ImageSliderContext'

export const useImageSliderContext = () => {
  const context = useContext(ImageSliderContext)
  if (context === undefined)
    throw new Error(
      `${ImageSliderContext.displayName} must be used within provider`,
    )

  return context
}

export default function ImageSliderProvider(props: {
  propertyId: number
  index?: number
  photoCount: number
  images: PropertyPhotoType[]
  children: React.ReactChild
}) {
  const {propertyId, images, photoCount, index, children} = props
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [currentImages, setCurrentImages] = useState<PropertyPhotoType[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const currentLoadedImagesCount = currentImages.length
  const hasMorePhotos = photoCount > currentLoadedImagesCount

  const loadMorePhotos = () => {
    setLoading(true)
    return publicGetLoadMorePhotosApi(propertyId, currentIndex + 1)
      .then((images) => setCurrentImages([...currentImages, ...images]))
      .finally(() => setLoading(false))
  }

  const loadAllPhotos = () => {
    setLoading(true)
    return publicGetLoadMorePhotosApi(propertyId, 0, 9999)
      .then((images) => setCurrentImages(images))
      .finally(() => setLoading(false))
  }

  //when we reach the end, load more photos
  useEffect(() => {
    if (currentImages.length === currentIndex + 1 && hasMorePhotos) {
      loadMorePhotos()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentImages, hasMorePhotos])

  //init
  useEffect(() => {
    setCurrentImages(images)
  }, [images])

  //init
  useEffect(() => {
    if (typeof index === 'number') {
      setCurrentIndex(index)
    }
  }, [index])

  return (
    <ImageSliderContext.Provider
      value={{
        currentIndex,
        images: currentImages,
        loading,
        photoCount,
        hasMorePhotos,
        setCurrentIndex,
        loadMorePhotos,
        loadAllPhotos,
        setLoading,
      }}
    >
      {children}
    </ImageSliderContext.Provider>
  )
}

export function Loading(props: {hidden?: boolean; size?: string}) {
  const {loading} = useImageSliderContext()

  if (!loading || props.hidden) {
    return null
  }

  return (
    <div className="absolute top-1/2 left-1/2 z-above-base transform -translate-x-1/2 -translate-y-1/2">
      <CircularProgress size={props.size} color="inherit" />
    </div>
  )
}
